import React from 'react';
import PropTypes from 'prop-types';
import { MultiOfferEntities } from 'client/data/models/multi-offer';

const getLogoData = offerDetails => {
  const { brand, brandAssets } = offerDetails;

  const { brandName, logo } = brandAssets ?? {};
  const { logoUrl, blackLogoUrl, logoHeightMultiplier } = logo ?? {};

  const resultBrandName = brandName || brand;
  const resultLogoHeightMultiplier = logoHeightMultiplier || 1.0;

  return {
    logoUrl,
    blackLogoUrl,
    logoHeightMultiplier: resultLogoHeightMultiplier,
    logoWidthMultiplier: 1.0 / resultLogoHeightMultiplier,
    logoAltText: `${resultBrandName} logo`,
  };
};

const getWidth = (height, logoWidthMultiplier) => height * logoWidthMultiplier;
const getHeight = (width, logoHeightMultiplier) => width * logoHeightMultiplier;

/**
 * Computes a balanced size, aiming that all the logos have the same area. This area equals to maxHeight * maxHeight
 * (i.e.the area of a square with a side of "maxHeight" pixels).
 * If the computed width/height is more than maxWidth/maxHeight, then we decrease the area so that the logo fits max size.
 *
 * @param {number} maxWidth maximum width.
 * @param {number} maxHeight maximum height.
 * @param {number} logoHeightMultiplier ratio (equals to height / width).
 * @returns {{width: number, height: number}} result width & height.
 */
const computeBalancedSize = ({ maxWidth, maxHeight }, logoHeightMultiplier) => {
  // This formula is derived from the following equalities:
  //  * area = width * height (NOTE: as the area of a rectangle)
  //  * area = maxHeight * maxHeight (NOTE: please check the js doc for this function)
  //  * width = height / logoHeightMultiplier (NOTE: since logoHeightMultiplier = height / width by definition)
  let height = Math.sqrt(logoHeightMultiplier) * maxHeight;

  if (height > maxHeight) {
    height = maxHeight;
  }

  let width = getWidth(height, 1 / logoHeightMultiplier);

  if (width > maxWidth) {
    width = maxWidth;
    height = getHeight(width, logoHeightMultiplier);
  }

  return { width, height };
};

export function BrandLogo({
  offerDetails,
  blackLogo = false,
  className = 'logo',
  containerClassName = '',
  width = null,
  height = null,
  balancedSize = null,
}) {
  let resultHeight = height;
  let resultWidth = width;

  const { logoUrl, blackLogoUrl, logoHeightMultiplier, logoWidthMultiplier, logoAltText } = getLogoData(offerDetails);

  if (!resultWidth && height) {
    resultWidth = getWidth(height, logoWidthMultiplier);
  }
  if (!resultHeight && width) {
    resultHeight = getHeight(width, logoHeightMultiplier);
  }
  if (balancedSize && !resultWidth && !resultHeight) {
    ({ width: resultWidth, height: resultHeight } = computeBalancedSize(balancedSize, logoHeightMultiplier));
  }

  const imageSrc = blackLogo ? blackLogoUrl : logoUrl;

  return imageSrc ? (
    <picture className={containerClassName}>
      <img className={className} src={imageSrc} alt={logoAltText} width={resultWidth} height={resultHeight} />
    </picture>
  ) : null;
}

BrandLogo.propTypes = {
  offerDetails: MultiOfferEntities.OfferDetails.isRequired,
  blackLogo: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  balancedSize: PropTypes.shape({
    maxWidth: PropTypes.number.isRequired,
    maxHeight: PropTypes.number.isRequired,
  }),
};
